import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loansApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, Phone, RefreshCw, Search, Loader2, CreditCard, Sparkles, UserCheck, X, ShieldAlert,
  Pencil, UserPlus, Car, StickyNote,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { DateRangeFilter, getDateRange } from '@/components/ui/DateRangeFilter';
import { formatDate, formatDateTime } from '@/utils/dateFormat';

import { StatusBadge } from '@/components/loans';
import { LoanStats } from '@/components/loans/LoanStats';
import { AssignExecutiveDialog } from '@/components/loans/AssignExecutiveDialog';
import { InspectionStatusModal } from '@/components/loans/InspectionStatusModal';
import { CityMultiFilter } from '@/components/loans/CityMultiFilter';
import { LeadActivityDrawer } from '@/components/loans/LeadActivityDrawer';
import LoanCockpit from '@/pages/LoanCockpit';

const STATUSES = ['NEW', 'INTERESTED', 'NOT_INTERESTED', 'RNR', 'CALL_BACK', 'FOLLOW_UP'];
const PAGE_SIZE = 20;

export default function LoansPage() {
  const { visibleTabs } = useAuth();
  const hasLoansTab = (visibleTabs || []).includes('loans');
  const deniedTab = (visibleTabs || []).length > 0 && !hasLoansTab;
  // The server decides who may reassign (card-counts.can_reassign) — no role list here.
  const [isManager, setIsManager] = useState(false);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [dateBypassed, setDateBypassed] = useState(false);
  const [counts, setCounts] = useState({});
  const [executives, setExecutives] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // Filters — a date range is always applied (defaults to this month)
  const initialRange = getDateRange('month');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState([]);
  const [dateRangeType, setDateRangeType] = useState('month');
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [page, setPage] = useState(0);

  // Selection + modals
  const [selected, setSelected] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null); // lead | 'bulk'
  const [assigning, setAssigning] = useState(false);
  const [statusLead, setStatusLead] = useState(null);
  const [inspectionLead, setInspectionLead] = useState(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [cockpitLeadId, setCockpitLeadId] = useState(null);
  const [activityLead, setActivityLead] = useState(null);   // { lead, tab }
  const fetchSeq = useRef(0);

  const filterParams = useCallback(() => {
    const p = {};
    if (activeStatus !== 'all' && activeStatus !== 'unassigned') p.status = activeStatus;
    if (search) p.search = search;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (activeStatus === 'unassigned') p.assigned_to = 'unassigned';
    else if (isManager && ownerFilter !== 'all') p.assigned_to = ownerFilter;
    if (cityFilter.length) p.cities = cityFilter.join(',');
    return p;
  }, [activeStatus, search, dateFrom, dateTo, ownerFilter, isManager, cityFilter]);

  const fetchAll = useCallback(async () => {
    if (deniedTab) return;  // restricted screen — don't fire calls that 403
    const reqId = ++fetchSeq.current;
    setLoading(true);
    try {
      const params = filterParams();
      const [leadsRes, countsRes] = await Promise.all([
        loansApi.getAll({ ...params, skip: page * PAGE_SIZE, limit: PAGE_SIZE }),
        loansApi.getCardCounts(params),
      ]);
      // A slower earlier request must not overwrite a newer one — typing a phone
      // number straight after load used to land on the initial month's result.
      if (reqId !== fetchSeq.current) return;
      setLeads(leadsRes.data.leads || []);
      setTotal(leadsRes.data.total || 0);
      setDateBypassed(!!leadsRes.data.date_filter_bypassed);
      setCounts(countsRes.data || {});
      setIsManager(!!countsRes.data?.can_reassign);
    } catch (err) {
      if (reqId === fetchSeq.current) toast.error(err.response?.data?.detail || 'Failed to load loan leads');
    } finally {
      if (reqId === fetchSeq.current) setLoading(false);
    }
  }, [filterParams, page, deniedTab]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadExecutives = useCallback(async () => {
    if (!isManager) return;  // desk-wide tallies are manager-only
    try {
      const res = await loansApi.getExecutives({ date_from: dateFrom, date_to: dateTo });
      setExecutives(res.data.executives || []);
    } catch (err) {
      /* executives list is only needed for assignment */
    }
  }, [dateFrom, dateTo, isManager]);

  useEffect(() => { loadExecutives(); }, [loadExecutives]);

  useEffect(() => {
    if (deniedTab) return;
    const { cities: _ignored, ...dateScope } = filterParams();
    loansApi.getCities(dateScope)
      .then((res) => setCityOptions(res.data.cities || []))
      .catch(() => {});
  }, [deniedTab, filterParams]);

  const applySearch = () => { setPage(0); setSearch(searchInput.trim()); };

  const resetFilters = () => {
    const r = getDateRange('month');
    setSearchInput(''); setSearch(''); setActiveStatus('all'); setOwnerFilter('all'); setCityFilter([]);
    setDateRangeType('month'); setDateFrom(r.from); setDateTo(r.to); setPage(0); setSelected([]);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await loansApi.syncCustomers();
      toast.success(res.data.message);
      fetchAll(); loadExecutives();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to sync customers');
    } finally {
      setSyncing(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!statusLead) return;
    setUpdatingStatus(true);
    try {
      await loansApi.update(statusLead.id, { status: newStatus, status_notes: statusNotes || null });
      toast.success('Status updated');
      setStatusLead(null); setStatusNotes('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const confirmAssign = async (executiveId) => {
    setAssigning(true);
    try {
      if (assignTarget === 'bulk') {
        const res = await loansApi.bulkAssign(selected, executiveId);
        toast.success(`${res.data.updated} lead(s) moved to ${res.data.assigned_to_name || 'nobody'}`);
        setSelected([]);
      } else {
        await loansApi.assign(assignTarget.id, executiveId);
        toast.success('Lead reassigned');
      }
      setAssignTarget(null);
      fetchAll(); loadExecutives();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign');
    } finally {
      setAssigning(false);
    }
  };

  const toggleRow = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allOnPageSelected = leads.length > 0 && leads.every((l) => selected.includes(l.id));
  const toggleAllOnPage = () => setSelected((s) => (
    allOnPageSelected ? s.filter((id) => !leads.some((l) => l.id === id)) : [...new Set([...s, ...leads.map((l) => l.id)])]
  ));

  const colCount = isManager ? 9 : 8;

  if (deniedTab) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-6 flex items-center justify-center" data-testid="loans-no-access">
        <div className="bg-white border rounded-xl p-10 text-center max-w-md">
          <ShieldAlert className="h-10 w-10 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900">Loans desk is restricted</h2>
          <p className="text-sm text-gray-500 mt-2">
            Only loan executives and the loans leadership can open this page. Ask HR if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-6" data-testid="loans-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Loans</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isManager ? 'Every loan lead across the desk' : 'Loan leads assigned to you'}
          </p>
        </div>
        {isManager && (
          <Button onClick={handleSync} disabled={syncing} variant="outline" data-testid="sync-customers-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Backfill Missing
          </Button>
        )}
      </div>

      <DateRangeFilter
        dateRangeType={dateRangeType}
        setDateRangeType={(v) => { setDateRangeType(v); setPage(0); }}
        dateFrom={dateFrom}
        setDateFrom={(v) => { setDateFrom(v); setPage(0); }}
        dateTo={dateTo}
        setDateTo={(v) => { setDateTo(v); setPage(0); }}
        showAllTime={false}
        className="mb-4"
      />

      <LoanStats
        counts={counts}
        activeStatus={activeStatus}
        unassignedActive={activeStatus === 'unassigned' || ownerFilter === 'unassigned'}
        showUnassigned={isManager}
        onStatusChange={(key) => { setActiveStatus(activeStatus === key ? 'all' : key); setPage(0); }}
        onUnassignedClick={() => { setActiveStatus(activeStatus === 'unassigned' ? 'all' : 'unassigned'); setPage(0); }}
      />

      <div className="bg-white rounded-xl border p-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] max-w-sm relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              className="pl-10"
              data-testid="loans-search-input"
            />
          </div>
          {isManager && (
            <Select value={ownerFilter} onValueChange={(v) => { setOwnerFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[230px]" data-testid="owner-filter-select">
                <SelectValue placeholder="All executives" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All executives</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {executives.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`owner-option-${e.id}`}>
                    {e.name} ({e.counts?.all || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <CityMultiFilter
            cities={cityOptions}
            selected={cityFilter}
            onChange={(v) => { setCityFilter(v); setPage(0); }}
          />
          <Button variant="outline" onClick={applySearch} data-testid="apply-filters-btn">
            <Search className="h-4 w-4 mr-2" /> Search
          </Button>
          <Button variant="ghost" onClick={resetFilters} data-testid="reset-filters-btn">
            <X className="h-4 w-4 mr-1" /> Reset
          </Button>
          <span className="ml-auto text-sm text-gray-500" data-testid="loans-total-count">
            {total.toLocaleString()} lead{total === 1 ? '' : 's'}
          </span>
        </div>
        {dateBypassed && (
          <p className="text-xs text-blue-700 mt-2.5" data-testid="loans-date-bypass-note">
            Searching a phone number looks across every date, not just the range above.
          </p>
        )}
      </div>

      {isManager && selected.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="bulk-bar">
          <span className="text-sm font-medium text-blue-900">{selected.length} selected</span>
          <Button size="sm" onClick={() => setAssignTarget('bulk')} data-testid="bulk-reassign-btn">
            <UserCheck className="h-4 w-4 mr-2" /> Reassign
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected([])} data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20" data-testid="loans-empty-state">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">No loan leads in this range</p>
            <p className="text-gray-400 text-sm mt-1">Widen the date range or clear the filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px]">
              <thead>
                <tr className="bg-gray-50 border-b">
                  {isManager && (
                    <th className="w-10 p-4">
                      <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAllOnPage} data-testid="select-all-checkbox" />
                    </th>
                  )}
                  <th className="text-left p-4 font-medium text-gray-600">Paid On</th>
                  <th className="text-left p-4 font-medium text-gray-600">Customer</th>
                  <th className="text-left p-4 font-medium text-gray-600">City</th>
                  <th className="text-left p-4 font-medium text-gray-600">Assigned To</th>
                  <th className="text-center p-4 font-medium text-gray-600">CIBIL</th>
                  <th className="text-center p-4 font-medium text-gray-600">Inspections</th>
                  <th className="text-left p-4 font-medium text-gray-600">Status</th>
                  <th className="text-center p-4 font-medium text-gray-600">Activity</th>
                  <th className="text-center p-4 font-medium text-gray-600">Profile</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {leads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50 transition-colors" data-testid={`loan-row-${lead.id}`}>
                    {isManager && (
                      <td className="p-4">
                        <Checkbox
                          checked={selected.includes(lead.id)}
                          onCheckedChange={() => toggleRow(lead.id)}
                          data-testid={`row-checkbox-${lead.id}`}
                        />
                      </td>
                    )}
                    <td className="p-4">
                      <p className="text-sm font-medium">{formatDate(lead.paid_at || lead.created_at)}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(lead.paid_at || lead.created_at)?.split(',')[1]}
                      </p>
                      {String(lead.created_at).slice(0, 10) !== String(lead.paid_at || lead.created_at).slice(0, 10) && (
                        <p className="text-[11px] text-gray-400 mt-0.5" title="When the loan lead was created">
                          added {formatDate(lead.created_at)}
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="font-medium text-gray-900">{lead.customer_name}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {lead.customer_phone || '—'}
                      </p>
                    </td>
                    <td className="p-4"><span className="text-sm text-gray-600">{lead.city_name || '-'}</span></td>
                    <td className="p-4" data-testid={`assigned-cell-${lead.id}`}>
                      {lead.assigned_to_name ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-gray-800">{lead.assigned_to_name}</span>
                          {isManager && (
                            <button
                              onClick={() => setAssignTarget(lead)}
                              title="Change executive"
                              className="p-1 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors"
                              data-testid={`reassign-btn-${lead.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ) : isManager ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAssignTarget(lead)}
                          className="h-7 px-2.5 text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                          data-testid={`reassign-btn-${lead.id}`}
                        >
                          <UserPlus className="h-3.5 w-3.5 mr-1" />
                          Assign
                        </Button>
                      ) : (
                        <span className="text-sm text-orange-600 font-medium">Unassigned</span>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      {lead.credit_score ? (
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold ${
                          lead.credit_score >= 750 ? 'bg-green-100 text-green-700'
                            : lead.credit_score >= 650 ? 'bg-yellow-100 text-yellow-700'
                            : lead.credit_score >= 550 ? 'bg-orange-100 text-orange-700'
                            : 'bg-red-100 text-red-700'
                        }`} data-testid="cibil-cell">
                          <CreditCard className="h-3.5 w-3.5" />{lead.credit_score}
                        </span>
                      ) : <span className="text-gray-400 text-sm">—</span>}
                    </td>
                    <td className="p-4 text-center" data-testid={`inspections-cell-${lead.id}`}>
                      <button
                        onClick={() => setInspectionLead(lead)}
                        className="inline-flex flex-col items-center gap-1 group"
                        data-testid={`inspections-btn-${lead.id}`}
                      >
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          (lead.inspection_summary?.label || '').includes('Published')
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 group-hover:bg-emerald-100'
                            : 'bg-slate-100 text-slate-600 border-slate-200 group-hover:bg-slate-200'
                        }`}>
                          <Car className="h-3 w-3" />
                          {lead.inspection_summary?.total
                            ? `${lead.inspection_summary.total} car${lead.inspection_summary.total > 1 ? 's' : ''} · ${lead.inspection_summary.label}`
                            : 'No inspection'}
                        </span>
                        {lead.loan_car_number && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">
                            loan: {lead.loan_car_number}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => { setStatusLead(lead); setStatusNotes(''); }}
                        className="hover:opacity-80 transition-opacity"
                        data-testid={`status-btn-${lead.id}`}
                      >
                        <StatusBadge status={lead.status} />
                      </button>
                      {lead.status_notes && (
                        <button
                          onClick={() => setActivityLead({ lead, tab: 'activity' })}
                          className="block text-left text-xs text-gray-500 mt-1 max-w-[150px] truncate hover:text-blue-600 hover:underline"
                          title="Read the full history"
                          data-testid={`status-note-${lead.id}`}
                        >
                          {lead.status_notes}
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-center" data-testid={`activity-cell-${lead.id}`}>
                      <button
                        onClick={() => setActivityLead({ lead, tab: 'notes' })}
                        className="relative inline-flex flex-col items-center gap-1 group"
                        title="Notes & activity"
                        data-testid={`loan-notes-button-${lead.id}`}
                      >
                        <span className="relative p-1.5 rounded-lg text-gray-500 group-hover:text-blue-600 group-hover:bg-blue-50 transition-colors">
                          <StickyNote className="h-4 w-4" />
                          {lead.activity_count > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                              {lead.activity_count > 99 ? '99+' : lead.activity_count}
                            </span>
                          )}
                        </span>
                        {lead.last_activity_at && (
                          <span className="text-[10px] text-gray-400 whitespace-nowrap">
                            {formatDate(lead.last_activity_at)}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <Button size="sm" onClick={() => setCockpitLeadId(lead.id)} data-testid="open-cockpit-btn">
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Customer Profile
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-gray-500">
              Showing {page * PAGE_SIZE + 1} - {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="prev-page-btn">
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)} data-testid="next-page-btn">
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!statusLead} onOpenChange={(o) => !o && setStatusLead(null)}>
        <DialogContent className="max-w-md" data-testid="status-modal">
          <DialogHeader>
            <DialogTitle>Update Lead Status</DialogTitle>
            <DialogDescription>
              {statusLead?.customer_name} - {statusLead?.customer_phone}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Reason</Label>
              <Input
                placeholder="Why — e.g. cx wants a call after Diwali"
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                className="mt-1"
                data-testid="status-notes-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Type the reason first — picking a status below saves straight away.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusUpdate(status)}
                  disabled={updatingStatus}
                  data-testid={`status-option-${status}`}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    statusLead?.status === status ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {status.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AssignExecutiveDialog
        open={!!assignTarget}
        onOpenChange={(o) => !o && setAssignTarget(null)}
        executives={executives}
        currentOwnerId={assignTarget && assignTarget !== 'bulk' ? assignTarget.assigned_to : null}
        allowClear={assignTarget === 'bulk'}
        leadCount={assignTarget === 'bulk' ? selected.length : 1}
        customerName={assignTarget && assignTarget !== 'bulk' ? assignTarget.customer_name : ''}
        busy={assigning}
        onConfirm={confirmAssign}
      />

      <InspectionStatusModal
        open={!!inspectionLead}
        onOpenChange={(o) => !o && setInspectionLead(null)}
        lead={inspectionLead}
        onSaved={fetchAll}
      />

      <LeadActivityDrawer
        lead={leads.find((l) => l.id === activityLead?.lead?.id) || activityLead?.lead}
        initialTab={activityLead?.tab || 'notes'}
        open={!!activityLead}
        onOpenChange={(o) => !o && setActivityLead(null)}
        onChanged={fetchAll}
      />

      {cockpitLeadId && (
        <LoanCockpit
          leadId={cockpitLeadId}
          onClose={() => { setCockpitLeadId(null); fetchAll(); }}
        />
      )}
    </div>
  );
}
